import React, { createContext, useContext, useState, useEffect } from 'react';
import { flushSync } from 'react-dom';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Saved choice first, then the visitor's system setting
const getInitialDarkMode = () => {
  try {
    const savedTheme = localStorage.getItem('darkMode');
    if (savedTheme !== null) return JSON.parse(savedTheme);
  } catch {
    // Storage can be blocked (private mode); fall through to the system setting
  }
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
};

// Browser chrome (mobile address bar) follows the page background
const THEME_COLORS = { light: '#FBF7EF', dark: '#080809' };

const applyTheme = (dark) => {
  document.documentElement.classList.toggle('dark', dark);
  document.querySelectorAll('meta[name="theme-color"]').forEach((meta) => {
    meta.setAttribute('content', dark ? THEME_COLORS.dark : THEME_COLORS.light);
  });
};

const prefersReducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

export const ThemeProvider = ({ children }) => {
  const [darkMode, setDarkMode] = useState(getInitialDarkMode);

  useEffect(() => {
    applyTheme(darkMode);
  }, [darkMode]);

  const toggleDarkMode = () => {
    const next = !darkMode;
    try {
      localStorage.setItem('darkMode', JSON.stringify(next));
    } catch {
      // Not saved; the toggle still works for this visit
    }

    // Cross-fade between themes where the browser supports it, so the page
    // doesn't flash from light to dark in a single frame
    if (!document.startViewTransition || prefersReducedMotion()) {
      setDarkMode(next);
      return;
    }
    document.startViewTransition(() => {
      flushSync(() => setDarkMode(next));
      applyTheme(next);
    });
  };

  return (
    <ThemeContext.Provider value={{ darkMode, toggleDarkMode }}>
      {children}
    </ThemeContext.Provider>
  );
};
